import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem('automat_token');
    if (savedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
    return savedToken;
  });
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Fetch subscription status after getting user
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const refreshSubscription = async () => {
    await fetchSubscriptionStatus();
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('automat_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, userType = 'end_user', paymentToken, trialToken = null, extraFields = {}) => {
    // payment_token is OPTIONAL in the new B2B model — only used for legacy paid flows
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      user_type: userType,
      payment_token: paymentToken || null,
      trial_token: trialToken || null,
      preferred_language: (typeof window !== 'undefined' ? localStorage.getItem('automat_language') : null) || 'it',
      ...extraFields,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('automat_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const registerProfessional = async (data, trialToken = null) => {
    // In the new model technician registration is FREE — payment_token is optional/legacy only
    const paymentToken = localStorage.getItem('payment_token');
    const effectiveTrialToken = trialToken || data.trial_token;
    
    const response = await axios.post(`${API}/auth/register-professional`, {
      ...data,
      payment_token: paymentToken || null,
      trial_token: effectiveTrialToken || null,
      preferred_language: (typeof window !== 'undefined' ? localStorage.getItem('automat_language') : null) || 'it'
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('automat_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('automat_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      registerProfessional,
      logout,
      updateUser,
      refreshSubscription,
      subscriptionStatus,
      isSubscriptionExpired: subscriptionStatus?.status === 'expired',
      needsRenewal: subscriptionStatus?.needs_renewal || false,
      isAuthenticated: !!user,
      isProfessional: user?.user_type === 'professional',
      isEndUser: user?.user_type === 'end_user',
      hasPaid: user?.has_paid || user?.is_trial
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
